import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { Keyboard, ScanLine } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ECRAN_TACTILE, useGarderFocus, useRafale } from "@/lib/douchette";

/**
 * Champ de saisie « prêt pour le lecteur » (Zebra TC21 / DataWedge, douchette).
 * - Il garde le curseur : le scan arrive toujours dedans.
 * - Un scan est validé tout seul, avec ou sans touche Entrée envoyée par le lecteur.
 * - Sur téléphone / terminal, il n'ouvre pas le clavier (inputMode « none ») ;
 *   le bouton clavier permet de taper un n° à la main (validé avec OK / Entrée).
 */
export const ChampLecteur = forwardRef(function ChampLecteur(
  { value, onChange, onSubmit, placeholder, testId, actif = true, className = "", boutonLabel = "OK", disabled = false }, ref,
) {
  const input = useRef(null);
  const [clavier, setClavier] = useState(!ECRAN_TACTILE);
  const [focus, setFocus] = useState(false);
  const valeur = useRef(value);
  valeur.current = value;
  const soumettre = useRef(onSubmit);
  soumettre.current = onSubmit;
  useImperativeHandle(ref, () => ({ focus: () => input.current?.focus() }));
  useGarderFocus(input, actif);
  // Scan sans Entrée : validé dès que le lecteur a fini d'écrire.
  const rafale = useRafale((code) => {
    if (!code) return;
    // le champ contenait déjà un ancien code : on ne garde que le nouveau
    if (code !== String(valeur.current || "").trim()) { onChange(code); setTimeout(() => soumettre.current(), 40); }
    else soumettre.current();
  });

  const basculerClavier = () => {
    setClavier((c) => !c);
    setTimeout(() => { input.current?.blur(); input.current?.focus(); }, 30);
  };

  return (
    <div>
      <form onSubmit={(e) => {
        e.preventDefault();
        // Entrée envoyée par le lecteur : on prend le code qu'il vient d'écrire
        const lu = rafale.enCours();
        rafale.annuler();
        if (lu && lu !== String(value || "").trim()) { onChange(lu); setTimeout(() => soumettre.current(), 40); }
        else onSubmit();
      }} className="flex gap-2">
        <div className="relative flex-1">
          <ScanLine size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${focus ? "text-emerald-500" : "text-muted-foreground"}`} />
          <Input
            ref={input}
            value={value}
            onChange={(e) => { const avant = valeur.current; onChange(e.target.value); rafale(avant, e.target.value); }}
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}
            placeholder={placeholder}
            inputMode={clavier ? "text" : "none"}
            autoComplete="off" autoCorrect="off" autoCapitalize="characters" spellCheck={false}
            className={`pl-9 font-mono h-11 ${focus ? "ring-2 ring-emerald-500/50" : ""} ${className}`}
            data-testid={testId}
          />
        </div>
        {ECRAN_TACTILE && (
          <Button type="button" variant={clavier ? "secondary" : "outline"} className="h-11 px-3" onClick={basculerClavier}
            title={clavier ? "Masquer le clavier" : "Taper un n° au clavier"} data-testid={`${testId}-clavier`}>
            <Keyboard size={16} />
          </Button>
        )}
        <Button type="submit" className="h-11" disabled={disabled || !String(value || "").trim()}>{boutonLabel}</Button>
      </form>
      <p className={`mt-1.5 text-xs flex items-center gap-1.5 ${focus ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground"}`} data-testid={`${testId}-etat`}>
        <span className={`inline-block w-2 h-2 rounded-full ${focus ? "bg-emerald-500 animate-pulse" : "bg-muted-foreground/50"}`} />
        {focus ? "Lecteur prêt : scannez directement" : "Touchez ici pour reprendre le scan"}
      </p>
    </div>
  );
});

/**
 * Pages sans champ de scan (liste des colis, fiche, dépôt) : champ invisible
 * qui garde le curseur pour recevoir le scan du lecteur, sans ouvrir le clavier.
 */
export function CapteurLecteur({ onScan, actif = true }) {
  const input = useRef(null);
  const [v, setV] = useState("");
  const valeur = useRef("");
  const cb = useRef(onScan);
  cb.current = onScan;
  useGarderFocus(input, actif);
  const envoyer = (lu) => {
    const code = (lu ?? valeur.current).trim();
    valeur.current = "";
    setV("");
    if (code.length >= 4) cb.current(code);
  };
  const rafale = useRafale((code) => envoyer(code));
  if (!actif) return null;
  return (
    <input
      ref={input}
      aria-hidden="true"
      tabIndex={-1}
      inputMode="none"
      autoComplete="off"
      value={v}
      onChange={(e) => { const avant = valeur.current; valeur.current = e.target.value; setV(e.target.value); rafale(avant, e.target.value); }}
      onKeyDown={(e) => {
        if (e.key !== "Enter") return;
        e.preventDefault();
        // Entrée : seulement à la fin d'une rafale du lecteur (pas une frappe lente au clavier)
        const lu = rafale.enCours();
        rafale.annuler();
        if (lu) envoyer(lu); else { valeur.current = ""; setV(""); }
      }}
      style={{ position: "fixed", left: 0, bottom: 0, width: 1, height: 1, opacity: 0, border: 0, padding: 0, pointerEvents: "none" }}
      data-testid="capteur-lecteur"
    />
  );
}
